import { getTag, sendNotification, useApiClient, useAsyncCache, useEntityMetadata, useSignalrTopic } from '@msdk/common'
import { EntityModel, OptionsetValue, PagedList } from '@msdk/definitions'
import { Badge, ButtonIcon, COLORS, createComponentWrapperWithRef, Popover, styled, uiTokens } from '@msdk/ui'
import { NotificationsNoneOutlined } from '@mui/icons-material'
import { NotificationPopup } from 'components/NotificationPopup'
import { useEffect, useState } from 'react'

import { NotificationsUnreadIcon } from './NotificationsUnreadIcon'

const MuiNavIconButton = styled(ButtonIcon)`
  display: flex;
  border-radius: 8px;
  padding: 8px;
  width: 64px;
  height: 56px;
  align-items: center;
  gap: 4px;
  align-self: stretch;
  background-color: transparent;
  margin-top: 4px;
  margin-bottom: 4px;
  color: white;

  &:hover {
    background-color: ${COLORS.blue900};
    box-shadow: unset;
  }
`

const NovaNavIconButton = styled(ButtonIcon)`
  display: flex;
  min-width: ${uiTokens.sizing.minInteractiveTarget}px;
  min-height: ${uiTokens.sizing.minInteractiveTarget}px;
  justify-content: center;
  align-items: center;
  gap: ${uiTokens.spacing.gap.small}px;
  color: ${uiTokens.uiContent.inverse};
  border-radius: ${uiTokens.radius.medium}px;

  &:hover,
  &[aria-expanded='true'] {
    background-color: ${uiTokens.color.blue[600]};
    color: ${uiTokens.uiContent.inverse};
    border-radius: ${uiTokens.radius.medium}px;
  }
`

export const NavIconButton = createComponentWrapperWithRef(
  {
    mui: MuiNavIconButton,
    nova: NovaNavIconButton,
  },
  'NavIconButton',
)

const MuiStyledBadge = styled(Badge)`
  width: 40px;
  height: 40px;
  padding: 8px;

  & .MuiBadge-badge {
    transform: translateX(calc(100% - 20px));
    font-weight: 700;
    padding: 0 6.5px;
  }
`

export const StyledBadge = createComponentWrapperWithRef(
  {
    mui: MuiStyledBadge,
    nova: styled(Badge)`
      .MuiBadge-badge {
        display: none;
      }
    `,
  },
  'StyledBadge',
)

const NovaStyledPopover = styled(Popover)`
  .MuiPopover-paper {
    background-color: transparent;
    border-radius: ${uiTokens.radius.xLarge}px;
    box-shadow: ${uiTokens.elevation.x[0]} ${uiTokens.elevation.y[500]}px ${uiTokens.elevation.blur[500]}px
      ${uiTokens.elevation.spread[500]}px ${uiTokens.uiSurface.effect.shadow};
  }
`

export const StyledPopover = createComponentWrapperWithRef(
  {
    mui: Popover,
    nova: NovaStyledPopover,
  },
  'StyledPopover',
)

let audio: HTMLAudioElement
function getAudio(): HTMLAudioElement {
  audio = audio ?? new Audio(`${window.__APP_CONFIG__.CLIENT}/notification_sound.wav`)
  return audio
}

export function NotificationIcon() {
  const api = useApiClient()
  const model = useEntityMetadata('NOTIFICATION') as EntityModel
  const cache = useAsyncCache()

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const [notificationCount, setNotificationCount] = useState(0)
  const [lastNotificationId, setLastNotificationId] = useState<string | null>(null)
  const [unreadNotificationCountLoading, setUnreadNotificationCountLoading] = useState<boolean>(false)

  const getNewNotification = async (shouldCache?: boolean) => {
    let res: PagedList
    if (shouldCache) {
      res = await cache.getOrCreate('NotificationIcon.NOTIFICATION_VIEW', () =>
        api.entity.getView('CMPS', model, 'NOTIFICATION_VIEW', {
          pageNumber: 0,
          pageSize: 1,
          filters: {
            '@NOTIFICATION_STATUS': new OptionsetValue('UNREAD', 'NOTIFICATION_STATUS_FILTER_OPT'),
          },
        }),
      )
    } else {
      cache.deleteEntry('NotificationIcon.NOTIFICATION_VIEW')
      res = await api.entity.getView('CMPS', model, 'NOTIFICATION_VIEW', {
        pageNumber: 0,
        pageSize: 1,
        filters: {
          '@NOTIFICATION_STATUS': new OptionsetValue('UNREAD', 'NOTIFICATION_STATUS_FILTER_OPT'),
        },
      })
    }
    const lastNotification = res.source && res.source.length ? res.source[0] : null
    return { totalItems: res.totalItems, lastNotification }
  }

  const updateNotificationCount = async (notify?: boolean, shouldCache?: boolean) => {
    setUnreadNotificationCountLoading(true)
    const res = await getNewNotification(shouldCache)
    const { lastNotification } = res

    //no last notification, the notification that has been saved it's not for this user
    //maybe the user does not have the configured role
    if (notify && lastNotification && lastNotificationId !== lastNotification.id) {
      setLastNotificationId(lastNotification.id)
      sendNotification({
        title: lastNotification.fields['TITLE']?.value,
        message: lastNotification.fields['BODY']?.value,
        status: 'info',
      })

      try {
        await getAudio().play()
        // Catch autoplay errors, if the user has not interacted with the page yes
        // the sound can't play because of browser permission blocking
      } catch (error) {
        console.error('[NotificationIcon] - error playing notification sound', error)
      }
    }

    if (notificationCount === res.totalItems) {
      setUnreadNotificationCountLoading(false)
    }
    setNotificationCount(res.totalItems)
  }

  useEffect(() => {
    setUnreadNotificationCountLoading(false)
  }, [notificationCount])

  useSignalrTopic('user-notification-saved', () => {
    updateNotificationCount(true).catch(console.error)
  })

  const handleOnClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const onNotificationUpdated = (refresh?: boolean) => {
    if (refresh) {
      updateNotificationCount().catch(console.error)
    } else {
      const newCount = notificationCount - 1
      setNotificationCount(newCount)
    }
  }

  useEffect(() => {
    updateNotificationCount(false, true).catch(console.error)
  }, [])

  return (
    <>
      <NavIconButton
        aria-label="notification"
        aria-expanded={!!anchorEl}
        onClick={handleOnClick}
        data-test-id={getTag('shell', 'header', 'notification', 'button')}
      >
        {notificationCount > 0 && (
          <StyledBadge badgeContent={notificationCount} color="error">
            <NotificationsUnreadIcon />
          </StyledBadge>
        )}
        {notificationCount === 0 && <NotificationsNoneOutlined />}
      </NavIconButton>
      <StyledPopover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <NotificationPopup
          onNotificationUpdated={onNotificationUpdated}
          unreadNotificationCount={notificationCount}
          unreadNotificationCountLoading={unreadNotificationCountLoading}
        />
      </StyledPopover>
    </>
  )
}
